import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faFileCsv, faFilePdf } from "@fortawesome/pro-solid-svg-icons";
import {
  AgGridEvent,
  CellValueChangedEvent,
  ColDef,
  ColGroupDef,
  ColumnRowGroupChangedEvent,
  ColumnState,
  ExcelExportParams,
  ExcelStyle,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GridApi,
  GridReadyEvent,
  IRowNode,
  ProcessCellForExportParams,
  RowGroupingDisplayType,
  SELECTION_COLUMN_ID,
  SideBarDef,
  ToolPanelDef,
  convertColumnState,
  GridState,
  FilterModel,
} from "ag-grid-community";
import { AgGridReactProps } from "ag-grid-react";
import { debounce, isEmpty, sortBy, startCase, isEqual } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { updateTableView, setTableView } from "actions";

import AgGrid from "components/AgGrid/AgGrid";
import { RowSelectionMode } from "components/AgGrid/constants";
import TableWrapper from "components/AgGrid/TableWrapper";
import { MultiButton, SlimSecondaryButton } from "components/Button";
import {
  ContextMenuPopoverButton,
  DefaultMultiButton,
} from "components/Button/MultiButton";
import { MultiButtonOption } from "components/Button/MultiButton/types";
import SearchInput from "components/Form/SearchInput";
import { Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import GlobalSearch from "components/SearchInput/GlobalSearch";

import { AgGridPanels, AgGridTables } from "constants/aggrid";
import { ApiModel } from "constants/loading";

import { CurrentAgencySelector } from "containers/ConnectedSelector";

import { EMPTY_ARRAY, EMPTY_OBJECT } from "lib";

import { buildRowSelectionOptions } from "lib/agGrid/buildRowSelectionOptions";
import {
  filterComponentToModelType,
  reconcileFilterModel,
} from "lib/agGrid/reconcileFilterModel";
import { exportAgGridToPdf, PdfOrientation } from "lib/agGridPdf";
import { getLivestockSaleId } from "lib/navigation";
import { pluralize } from "lib/pluralize";

import {
  getCurrentSale,
  getEffectiveSaleTitleBySaleId,
  getSavedViewById,
  getTableSetting,
  getSelectedViewIdByTable,
  getSavedViewsByTable,
} from "selectors";

import { useIsMobile } from "hooks";
import { useAgGridHeaderWidthFix } from "hooks/useAgGridHeaderWidthFix";
import { useHandleAgGridColumnDefs } from "hooks/useHandleAgGridColumnDefs";

import SavedViewsPanel from "./panels/SavedViewsPanel";
import { ViewSelector } from "./ViewSelector";

const selectionColumnDef: ColDef = {
  width: 40,
  pinned: "left",
};

export const globalDefaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: "agMultiColumnFilter",
  enableCellChangeFlash: true,
  floatingFilter: true,
  // Without this, ungrouped columns stretch to fill the full multi-level header height.
  suppressSpanHeaderHeight: true,
};

const noUnpinnedColumns = () => [];

const ignoredSaveEventSources = [
  "api",
  "autosizeColumns",
  "gridOptionsChanged",
  "gridInitializing",
];

// Generic type to add source to the base AgGridEvent type - we have a generic callback that just
// checks the source - which is on most events - but not the base.
type AgGridEventWithSource = AgGridEvent & { source?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeFilterCondition(
  filterCondition: unknown,
): Record<string, unknown> | null {
  if (!isRecord(filterCondition)) {
    return null;
  }

  if (!filterCondition.operator) {
    return filterCondition;
  }

  const { condition1, condition2, conditions, ...rest } = filterCondition;
  const normalizedConditions = (
    Array.isArray(conditions) ? conditions : [condition1, condition2]
  )
    .map(normalizeFilterCondition)
    .filter(isRecord);

  if (normalizedConditions.length === 0) {
    return null;
  }

  if (normalizedConditions.length === 1) {
    return normalizedConditions[0];
  }

  return {
    ...rest,
    conditions: normalizedConditions,
  };
}

function normalizeFilterModel(
  filterModel: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (!filterModel) {
    return null;
  }

  const normalizedFilterModel = Object.entries(filterModel).reduce<
    Record<string, unknown>
  >((acc, [columnId, filterCondition]) => {
    const normalizedFilterCondition = normalizeFilterCondition(filterCondition);
    if (normalizedFilterCondition) {
      acc[columnId] = normalizedFilterCondition;
    }
    return acc;
  }, {});

  return isEmpty(normalizedFilterModel) ? null : normalizedFilterModel;
}

const excelStyles: ExcelStyle[] = [
  {
    id: "horizontal-center",
    alignment: {
      horizontal: "Center",
    },
  },

  {
    id: "bold",
    font: {
      bold: true,
    },
  },
];

function applyTableStateChanges(
  api: GridReadyEvent["api"],
  state: ColumnState[] | null | undefined,
  filterModel: Record<string, unknown> | null | undefined,
): void {
  if (!api) {
    return;
  }
  if (state) {
    api.applyColumnState({
      state,
      applyOrder: true,
      // If the column isn't defined in the saved view, this is what it falls to.
      // (A bit of a smell but some older views have older cruft in them)
      defaultState: { rowGroup: false, hide: true },
    });
  }

  // Saved views may contain pre-v29.2 combined filter models using condition1/condition2.
  // AG Grid v35 floating filters expect conditions[] when operator is present.
  const normalizedFilterModel = normalizeFilterModel(filterModel);

  // Drop/reshape filter models whose column has since changed filter type.
  const reconciledFilterModel = reconcileFilterModel(
    normalizedFilterModel,
    colId =>
      filterComponentToModelType(api.getColumn(colId)?.getColDef().filter),
  );
  api.setFilterModel(reconciledFilterModel);
}

// Tables that ship a fixed mobile column layout (a different columnDefs array
// is passed on mobile). Saved-view application is suppressed on mobile for
// these tables so the fixed order isn't overridden by a global default view
// or a view the user previously selected on desktop.
const TABLES_WITH_FIXED_MOBILE_LAYOUT = new Set<string>([
  AgGridTables.BUSINESSES,
  AgGridTables.MANUAL_ADJUSTMENT,
]);

interface StableSavedViewStates {
  state: ColumnState[] | null;
  filters: FilterModel;
  initialDirty: boolean;
  initialState: GridState;
}

function useGetSavedViewStates(
  tableName: string,
  selectionColumnVisible: boolean,
): StableSavedViewStates {
  const tableSettings = useSelector(getTableSetting(tableName));
  const dispatch = useDispatch();
  const tableState = tableSettings?.state;
  const tableFilters = tableSettings?.filters;

  // If we have a view selected in state - use that - otherwise use the default view (as defined by either
  // one with "default" in the name, or the first view in the list.)
  const selectedSavedViewId: string | null = useSelector(
    getSelectedViewIdByTable(tableName),
  );

  const defaultSavedViewId: string | null = useSelector((state: RootState) => {
    const globalViews = getSavedViewsByTable(tableName)(state)?.filter(
      (view: SavedView) => view.isGlobal,
    );
    const defaultView = globalViews?.find((view: SavedView) =>
      view.name.toLowerCase().includes("default"),
    );
    return defaultView?.id || globalViews?.[0]?.id || null;
  });

  const savedViewId: string | null =
    selectedSavedViewId || defaultSavedViewId || null;
  const savedView: SavedView | null = useSelector(
    getSavedViewById(savedViewId),
  );

  if (!selectedSavedViewId && defaultSavedViewId) {
    dispatch(setTableView(tableName, defaultSavedViewId));
  }

  const stableState: StableSavedViewStates = useMemo(() => {
    const parsedSavedViewContent = JSON.parse(savedView?.content || "[]");
    // Saved-view / redux content only - excludes the operational selection column.
    const persistedState: ColumnState[] = tableState
      ? [...tableState]
      : parsedSavedViewContent;

    // Post-init applyColumnState needs to know about the selection column so it
    // doesn't get reordered out, but its pinning is set via the
    // `selectionColumnDef` grid prop, not via saved-view content.
    const state: ColumnState[] = [...persistedState];
    if (
      selectionColumnVisible &&
      !state.some((col: ColumnState) => col.colId === SELECTION_COLUMN_ID)
    ) {
      state.push({ colId: SELECTION_COLUMN_ID, hide: false, pinned: "left" });
    }

    const filters: FilterModel = tableFilters || savedView?.filters || {};

    // initialState is built from `persistedState` (pre-selection-push). If the
    // selection column ends up in initialState.columnPinning.leftColIds, AG
    // Grid sets `shouldSetColumnPinningState=true` in its internal
    // setColumnState, which short-circuits partialColumnState and forces
    // `defaultState.pinned = null` - unpinning every colDef-pinned column at
    // init. The selection column is already pinned via its `selectionColumnDef`
    // prop, so it doesn't need to be in initialState.
    //
    // partialColumnState: true tells AG Grid to leave column attributes alone
    // when initialState doesn't speak to them (so colDef defaults win for any
    // attribute the saved view doesn't positively override).
    const initialState: GridState = { partialColumnState: true };
    if (persistedState.length > 0) {
      Object.assign(initialState, convertColumnState(persistedState));
    }
    // Filters are applied post-init via applyTableStateChanges (which reconciles
    // stale shapes), not seeded into initialState - AG Grid applies initialState
    // during construction, before reconciliation can run.

    // Is the current in (redux) column state different to the saved view content?
    // Notes:
    // - we're diffing PRE adding the selection column - that's operational not part of the saved view content.
    // - filter down to just visible columns - getColumnState gives us all columns, with hide=true
    // - saved views have been in and out of a JSON -> string -> JSON.  This removed undefined values!  Do the same to our local state.
    // - there are some extra fields coming in from the newer getColumnState call - normalize them out for this (blunt) comparison.

    const normalizedVisibleTableState = JSON.parse(
      JSON.stringify(
        tableState
          ?.filter(
            (col: ColumnState) =>
              !col.hide && col.colId !== SELECTION_COLUMN_ID,
          )
          .map(({ sortType, ...col }: ColumnState) => col) || [],
      ),
    );

    const normalizedSavedViewContent = parsedSavedViewContent.filter(
      (col: ColumnState) => !col.hide && col.colId !== SELECTION_COLUMN_ID,
    );

    const columnsMatch = isEqual(
      normalizedVisibleTableState,
      normalizedSavedViewContent,
    );

    const initialDirty = !columnsMatch || !isEqual(savedView?.filters, filters);

    // If no columns are visible - set to null - this stops us calling "applyColumnState" with an empty array, and shows ALL columns.
    const stateHasMoreThanSelectedColumn =
      state.filter(col => col.colId !== SELECTION_COLUMN_ID).length > 0;
    return {
      state: stateHasMoreThanSelectedColumn ? state : null,
      filters,
      initialDirty,
      initialState,
    };
  }, [savedView, tableState, tableFilters, selectionColumnVisible]);
  return stableState;
}

function useAgGridViewStateStore(tableName: string) {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const tableSettings = useSelector(getTableSetting(tableName));
  const tableViewId = tableSettings?.viewId || null;

  const skipViewOnMobile =
    isMobile && TABLES_WITH_FIXED_MOBILE_LAYOUT.has(tableName);

  const saveTableState = useCallback(
    (event: AgGridEventWithSource) => {
      // Tables that drive their own mobile layout should never persist mobile
      // column state — otherwise a mobile resize event would clobber the
      // user's desktop saved view.
      if (skipViewOnMobile && isMobile) {
        return;
      }
      const api = event?.api;
      if (api) {
        const colState = api.getColumnState();
        const filterModel = api.getFilterModel();

        // In some race conditions, getColumnState returns an empty array,
        // avoid saving an empty column state to current table view
        if (colState.length > 0) {
          // Dispatch the change to state if our columns or filters have changed.
          dispatch(
            updateTableView(tableName, tableViewId, {
              state: colState,
              filters: isEmpty(filterModel) ? {} : filterModel,
            }),
          );
        }
      }
    },
    [dispatch, skipViewOnMobile, tableName, tableViewId, isMobile],
  );

  return saveTableState;
}

function useGetSideBar(
  tableName: string,
  hideSideBar: boolean,
  panels: ToolPanelDef[] | null,
  defaultToolPanel: string | null,
) {
  const sideBar = useMemo(() => {
    if (hideSideBar) {
      return null;
    }
    const sideBarPanels: SideBarDef = {
      toolPanels: [
        {
          id: AgGridPanels.COLUMNS,
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            // tool panel columns won't move when columns are reordered in the grid
            suppressSyncLayoutWithGrid: true,
            // prevents columns being reordered from the columns tool panel
            suppressColumnMove: true,
          },
        },
        {
          id: AgGridPanels.FILTERS,
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },

        {
          id: AgGridPanels.SAVED_VIEWS,
          labelDefault: "Views",
          labelKey: "savedViews",
          iconKey: "save",
          toolPanel: SavedViewsPanel,
          toolPanelParams: {
            tableName,
          },
        },
      ],
      defaultToolPanel,
    };
    if (panels) {
      sideBarPanels.toolPanels.push(...panels);
    }
    return sideBarPanels;
  }, [hideSideBar, tableName, panels, defaultToolPanel]);

  return sideBar;
}

function useGetAdditionalActionButtons(
  agGridInstance: GridReadyEvent | null,
  additionalActions: MultiButtonOption[] | null,
  hasExportCsvPermissions: boolean,
  customExport: MultiButtonOption[] | null,
  multiButtonComponent: React.ComponentType,
  downloadFilename: string | null,
  csvExportOptions: Record<string, unknown> | null,
  exportSaleTitle: string | null,
  tableName: string,
) {
  const onBtnExport = useCallback(() => {
    if (!agGridInstance) {
      return;
    }
    const params = {
      fileName: downloadFilename,
      // v33+ changed the default to export formatted values; restore v29 behaviour
      // by exporting raw cell values (after valueGetter, before valueFormatter).
      processCellCallback: (p: ProcessCellForExportParams) =>
        p.value == null ? "" : String(p.value),
      ...csvExportOptions,
    };
    if (agGridInstance.api.getLastDisplayedRowIndex() !== -1) {
      agGridInstance.api.exportDataAsCsv(params);
    }
  }, [agGridInstance, downloadFilename, csvExportOptions]);

  const saleId = getLivestockSaleId();
  const saleTitleFromSale = useSelector(getEffectiveSaleTitleBySaleId(saleId));
  const currentSale = useSelector(getCurrentSale);
  const saleTitle = [
    currentSale?.sale_code,
    saleTitleFromSale,
    exportSaleTitle || pluralize(startCase(tableName), 2),
  ]
    .filter(Boolean)
    .join(" - ");

  const additionalActionButtons = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- additionalActions is intentionally loosely typed; see interface comment
    const actions: any[] = [...(additionalActions || [])];
    if (hasExportCsvPermissions) {
      actions.push(
        ...(customExport || [
          {
            title: "Export CSV",
            isDisabled: !agGridInstance,
            onClick: onBtnExport,
            icon:
              multiButtonComponent === DefaultMultiButton ? faFileCsv : null,
          },
        ]),
        {
          title: "Download as PDF (portrait)",
          isDisabled: !agGridInstance,
          onClick: () =>
            exportAgGridToPdf(agGridInstance.api, {
              orientation: PdfOrientation.PORTRAIT,
              saleTitle,
            }),
          icon: multiButtonComponent === DefaultMultiButton ? faFilePdf : null,
        },
        {
          title: "Download as PDF (landscape)",
          isDisabled: !agGridInstance,
          onClick: () =>
            exportAgGridToPdf(agGridInstance.api, {
              orientation: PdfOrientation.LANDSCAPE,
              saleTitle,
            }),
          icon: multiButtonComponent === DefaultMultiButton ? faFilePdf : null,
        },
      );
    }
    return actions;
  }, [
    agGridInstance,
    additionalActions,
    onBtnExport,
    customExport,
    hasExportCsvPermissions,
    multiButtonComponent,
    saleTitle,
  ]);

  return additionalActionButtons;
}

const ActionItems = styled.div(
  ({ theme }) => `
  display: flex;
  gap: ${theme.space[1]}px;
  margin-left: auto;
  order: 1;
  align-items: center;

  @media (min-width: ${theme.breakpoints[1]}px) {
    order: 2;
  }

  @media (min-width: ${theme.breakpoints[3]}px) {
    margin-left: 0;
  }
`,
);

const SearchItems = styled.div(
  ({ theme }) => `
  display: flex;
  gap: ${theme.space[1]}px;
  order: 2;
  flex-grow: 1;
  flex-shrink: 1;
  min-width: 300px;
  justify-content: flex-end;
  width: 100%;
  min-width: 90px;
  align-items: baseline;
  @media (min-width: ${theme.breakpoints[1]}px) {
    margin-left: auto;
    order: 1;
    width: auto;
    max-width: 650px;
  }
`,
);
const RoundFilterContainer = styled.div`
  margin-right: auto;
  order: 0;
`;

interface AgGridTableProps {
  // Callers pass heterogeneous button types: MultiButtonOption, MultiButtonActionButton (global.d.ts),
  // and objects with React element titles. Normalize to MultiButtonOption[] in a follow-up.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalActions?: any[];
  /**
   * Called whenever the view dirty state changes. Must be a stable reference
   * (e.g. useState setter or useCallback with []) — it lives in a useEffect
   * dependency array and an unstable reference will cause the effect to re-run
   * and overwrite any user-set dirty state back to initialDirty.
   */
  onSetViewDirty?: (isDirty: boolean) => void;
  aggFuncs?: AgGridReactProps["aggFuncs"];
  doesExternalFilterPass?: AgGridReactProps["doesExternalFilterPass"];
  context?: Record<string, unknown>;
  defaultColDef?: ColDef;
  columnDefs: (ColDef | ColGroupDef)[];
  isExternalFilterPresent?: () => boolean;
  isRowSelectable?: (node: IRowNode) => boolean;
  rowData?: unknown[];
  loading?: AgGridReactProps["loading"];
  downloadFilename?: string;
  tableName: string;
  rowSelection?: RowSelectionMode;
  onRowSelectionChanged?: ((rows: unknown[]) => void) | null;
  editSelected?: (() => void) | null;
  getRowId?: AgGridReactProps["getRowId"];
  autoSizeColumnIds?: string[];
  onFilterChangedExtra?: (event: FilterChangedEvent) => void;
  extraSearchComponents?: React.ReactNode;
  extraActionComponents?: React.ReactNode;
  getRowStyle?: AgGridReactProps["getRowStyle"];
  groupDisplayType?: RowGroupingDisplayType;
  groupIncludeFooter?: boolean;
  grandTotalRow?: AgGridReactProps["grandTotalRow"];
  suppressAggFuncInHeader?: boolean;
  getContextMenuItems?: AgGridReactProps["getContextMenuItems"];
  onCellValueChanged?: (event: CellValueChangedEvent) => void;
  onGridReady?: (event: GridReadyEvent) => void;
  pagination?: boolean;
  paginationPageSize?: number;
  paginationAutoPageSize?: boolean;
  statusBar?: AgGridReactProps["statusBar"];
  stopEditingWhenCellsLoseFocus?: boolean;
  singleClickEdit?: boolean;
  showGlobalSearchFilter?: boolean;
  showTextFilter?: boolean;
  showSimpleAgencyFilter?: boolean;
  panels?: ToolPanelDef[] | null;
  rowClassRules?: AgGridReactProps["rowClassRules"];
  csvExportOptions?: Record<string, unknown>;
  isGroupOpenByDefault?: AgGridReactProps["isGroupOpenByDefault"];
  customExport?: MultiButtonOption[] | null;
  hideHeader?: boolean;
  WrapperComponent?: React.ComponentType<{
    "data-tour"?: string;
    children?: React.ReactNode;
  }>;
  masterDetail?: boolean;
  detailCellRendererParams?: Record<string, unknown>;
  hideSideBar?: boolean;
  getRowHeight?: AgGridReactProps["getRowHeight"];
  rowBuffer?: number;
  suppressColumnVirtualisation?: boolean;
  hasExportCsvPermissions?: boolean;
  suppressCellSelection?: boolean;
  hideSavedViews?: boolean;
  // Shown regardless of saved views or stored table state, which hide any
  // column they don't mention -- for columns added after a view was saved.
  alwaysVisibleColIds?: string[];
  multiSelectActionOnly?: boolean;
  defaultCsvExportParams?: Record<string, unknown>;
  defaultExcelExportParams?: ExcelExportParams;
  rowsSelectable?: boolean;
  enableBrowserTooltips?: boolean;
  defaultToolPanel?: string | null;
  multiButtonComponent?: React.ComponentType;
  initialQuickFilterText?: string;
  exportSaleTitle?: string | null;
  enableClickSelection?: boolean;
}

const AgGridTable = ({
  additionalActions,
  onSetViewDirty = undefined,
  aggFuncs,
  doesExternalFilterPass,
  context = EMPTY_OBJECT as Record<string, unknown>,
  defaultColDef,
  columnDefs,
  isExternalFilterPresent = undefined,
  isRowSelectable = undefined,
  rowData,
  loading = false,
  downloadFilename,
  tableName,
  rowSelection = undefined,
  onRowSelectionChanged = null,
  editSelected = null,
  getRowId = undefined,
  autoSizeColumnIds = undefined,
  onFilterChangedExtra,
  extraSearchComponents = EMPTY_ARRAY,
  extraActionComponents = EMPTY_ARRAY,
  getRowStyle,
  groupDisplayType,
  groupIncludeFooter = false,
  grandTotalRow = undefined,
  suppressAggFuncInHeader = true,
  getContextMenuItems = undefined,
  onCellValueChanged = undefined,
  onGridReady,
  pagination = true,
  paginationPageSize = 200,
  paginationAutoPageSize = false,
  statusBar = undefined,
  stopEditingWhenCellsLoseFocus = undefined,
  singleClickEdit = undefined,
  showGlobalSearchFilter = true,
  showTextFilter = false,
  showSimpleAgencyFilter = false,
  panels = null,
  rowClassRules = undefined,
  csvExportOptions = EMPTY_OBJECT as Record<string, unknown>,
  isGroupOpenByDefault,
  customExport = null,
  hideHeader = false,
  WrapperComponent = TableWrapper,
  masterDetail = false,
  detailCellRendererParams = EMPTY_OBJECT as Record<string, unknown>,
  hideSideBar = false,
  getRowHeight = undefined,
  rowBuffer = 10,
  suppressColumnVirtualisation = false,
  hasExportCsvPermissions = true,
  suppressCellSelection = false,
  hideSavedViews = false,
  alwaysVisibleColIds = EMPTY_ARRAY,
  multiSelectActionOnly = false,
  defaultCsvExportParams = EMPTY_OBJECT as Record<string, unknown>,
  defaultExcelExportParams = undefined,
  rowsSelectable = false,
  enableBrowserTooltips = undefined,
  defaultToolPanel = null,
  multiButtonComponent = DefaultMultiButton,
  initialQuickFilterText = "",
  exportSaleTitle = null,
  enableClickSelection = true,
}: AgGridTableProps) => {
  const isMobile = useIsMobile();

  const quickFilterDebounceTime = 400;

  const [quickFilterText, setQuickFilterText] = useState(
    initialQuickFilterText,
  );
  const [quickFilterValue, setQuickFilterValue] = useState(
    initialQuickFilterText,
  );
  const onQuickFilterChange = (value: string) => {
    setQuickFilterText(value);
    debounce(() => setQuickFilterValue(value), quickFilterDebounceTime)();
  };

  const sideBar = useGetSideBar(
    tableName,
    hideSideBar,
    panels,
    defaultToolPanel,
  );

  // Stash a copy of the agGrid instance so we can do things with it later.
  const [agGridInstance, setAgGridInstance] = useState<GridReadyEvent | null>(
    null,
  );
  const onInternalGridReady = useCallback(
    (agGrid: GridReadyEvent) => {
      setAgGridInstance(agGrid);

      typeof onGridReady === "function" && onGridReady(agGrid);
    },
    [onGridReady],
  );

  const onToolPanelVisibleChanged = useAgGridHeaderWidthFix(tableName);

  // update column defs based if we are on mobile
  const mergedDefaultColDef = useMemo(
    () => ({ ...globalDefaultColDef, ...defaultColDef }),
    [defaultColDef],
  );
  const handledColumnDefs = useHandleAgGridColumnDefs(columnDefs);

  // Fire a save of the state only when the grid is destroyed.
  const saveTableState = useAgGridViewStateStore(tableName);

  // When the saved view state changes, and/or the agGridInstance is set (see onIntnernalGridReady above, reset the state and filters from it.
  const stableSavedViewStates = useGetSavedViewStates(
    tableName,
    rowsSelectable,
  );
  const [isViewStateDirty, setIsViewStateDirty] = useState(
    stableSavedViewStates.initialDirty,
  );

  useEffect(() => {
    if (stableSavedViewStates) {
      applyTableStateChanges(
        agGridInstance?.api,
        stableSavedViewStates.state,
        stableSavedViewStates.filters,
      );
      if (alwaysVisibleColIds.length) {
        agGridInstance?.api?.applyColumnState({
          state: alwaysVisibleColIds.map(colId => ({ colId, hide: false })),
        });
      }
      setIsViewStateDirty(stableSavedViewStates.initialDirty);
      if (onSetViewDirty) {
        onSetViewDirty(stableSavedViewStates.initialDirty);
      }
    }
  }, [
    stableSavedViewStates,
    agGridInstance,
    onSetViewDirty,
    alwaysVisibleColIds,
  ]);

  // Attached to multiple onXChanged events in AgGrid - onStateUpdate doesn't provide
  // the source which is the safest way to determine whether a user, api or initializtion
  // changed things.
  const markGridViewDirty = useCallback(
    (event: AgGridEventWithSource) => {
      const eventSource = event?.source;
      // When a user changes anything mark the view as dirty - explicitly ignore api calls.
      if (eventSource && !ignoredSaveEventSources.includes(eventSource)) {
        setIsViewStateDirty(true);
        if (onSetViewDirty) {
          onSetViewDirty(true);
        }
      }
    },
    [setIsViewStateDirty, onSetViewDirty],
  );

  // Allow some (legacy?) configurable callbacks.
  const onFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      // Call the dirty check
      // Note - if we ever remmove this callback we still need to fire this call.
      markGridViewDirty(event);
      typeof onFilterChangedExtra === "function" && onFilterChangedExtra(event);
    },
    [onFilterChangedExtra, markGridViewDirty],
  );

  const autoSizeColumnsOnMobile =
    isMobile && TABLES_WITH_FIXED_MOBILE_LAYOUT.has(tableName);

  const autoSizeRequestedColumns = useCallback(
    (api: GridApi) => {
      if (autoSizeColumnIds?.length) {
        api.autoSizeColumns(autoSizeColumnIds);
      }
    },
    [autoSizeColumnIds],
  );

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent) => {
      if (autoSizeColumnsOnMobile) {
        event.api.autoSizeAllColumns();
      }
      autoSizeRequestedColumns(event.api);
    },
    [autoSizeColumnsOnMobile, autoSizeRequestedColumns],
  );

  const onColumnRowGroupChanged = useCallback(
    (event: ColumnRowGroupChangedEvent) => {
      markGridViewDirty(event);
      // Group rows can render different cells to leaf rows, so a width measured
      // before grouping no longer fits after it.
      autoSizeRequestedColumns(event.api);
    },
    [markGridViewDirty, autoSizeRequestedColumns],
  );

  // Keep a record of selected rows - largely for a count on the bulk edit button.
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const handleSelectionChanged = useCallback(() => {
    if (!agGridInstance) return;
    const sortedNodes = sortBy(
      agGridInstance.api.getSelectedNodes(),
      "rowIndex",
    );
    setSelectedRowIds(
      sortedNodes.map((node: IRowNode) => node.id).filter(Boolean),
    );
    typeof onRowSelectionChanged === "function" &&
      onRowSelectionChanged(sortedNodes.map((node: IRowNode) => node.data));
  }, [agGridInstance, onRowSelectionChanged]);

  const additionalActionButtons = useGetAdditionalActionButtons(
    agGridInstance,
    additionalActions,
    hasExportCsvPermissions,
    customExport,
    multiButtonComponent,
    downloadFilename,
    csvExportOptions,
    exportSaleTitle,
    tableName,
  );

  const sharedContext = useMemo(
    () => ({
      ...context,
      rowsSelectable,
      tableName,
    }),
    [context, rowsSelectable, tableName],
  );

  // v32+ warns when paginationPageSize is not included in paginationPageSizeSelector.
  // Build a selector list that always contains the active page size.
  const paginationPageSizeSelector = useMemo(() => {
    const base = [20, 50, 100, 200];
    if (base.includes(paginationPageSize)) return base;
    return [...base, paginationPageSize].sort((a, b) => a - b);
  }, [paginationPageSize]);

  const rowSelectionOptions = useMemo(
    () =>
      buildRowSelectionOptions(
        rowSelection,
        rowsSelectable,
        isRowSelectable,
        enableClickSelection,
      ),
    [rowSelection, rowsSelectable, isRowSelectable, enableClickSelection],
  );
  return (
    <>
      {!hideHeader && (
        <Row fillWidth flexWrap fullWidth gridGap={1} padding={1}>
          {!hideSavedViews && tableName && (
            <RoundFilterContainer>
              <div data-tour="table-view">
                <ViewSelector
                  tableName={tableName}
                  isDirty={isViewStateDirty}
                />
              </div>
            </RoundFilterContainer>
          )}
          {((showGlobalSearchFilter && getLivestockSaleId()) ||
            showTextFilter ||
            showSimpleAgencyFilter ||
            extraSearchComponents) && (
            <SearchItems>
              {showGlobalSearchFilter && getLivestockSaleId() && (
                <GlobalSearch />
              )}

              {showTextFilter && (
                <div data-tour="quick-filter">
                  <SearchInput
                    value={quickFilterText}
                    onChange={onQuickFilterChange}
                  />
                </div>
              )}

              {extraSearchComponents && extraSearchComponents}
            </SearchItems>
          )}

          <ActionItems>
            {extraActionComponents && extraActionComponents}

            {showSimpleAgencyFilter && (
              <WaitForSync requiredData={[ApiModel.AGENCIES]}>
                <CurrentAgencySelector includeAll />
              </WaitForSync>
            )}

            {multiSelectActionOnly ? (
              <ContextMenuPopoverButton
                ButtonComponent={multiButtonComponent}
                buttons={additionalActionButtons}
                optionsButtonDataTour={`${tableName}-optionsButton`}
              />
            ) : (
              <MultiButton
                buttons={additionalActionButtons}
                ButtonComponent={multiButtonComponent}
                buttonGroupDataTour={`${tableName}-action-buttons`}
              />
            )}
            {typeof editSelected === "function" && (
              <SlimSecondaryButton
                data-tour={pluralize(
                  `edit${selectedRowIds.length}SelectedItem`,
                  selectedRowIds.length,
                )}
                onClick={editSelected}
                disabled={selectedRowIds.length === 0}
              >
                Edit {selectedRowIds.length} selected item
                {selectedRowIds.length !== 1 && "s"}
              </SlimSecondaryButton>
            )}
          </ActionItems>
        </Row>
      )}

      <WrapperComponent data-tour={tableName}>
        <AgGrid
          autoSizePadding={2}
          aggFuncs={aggFuncs}
          columnDefs={handledColumnDefs}
          defaultColDef={mergedDefaultColDef}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          quickFilterText={quickFilterValue}
          groupDisplayType={groupDisplayType}
          groupTotalRow={groupIncludeFooter ? "bottom" : undefined}
          grandTotalRow={grandTotalRow}
          isGroupOpenByDefault={isGroupOpenByDefault}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          rowBuffer={rowBuffer}
          onFilterChanged={onFilterChanged}
          // Changes that maybe of interest to the saved views.
          onColumnMoved={markGridViewDirty}
          onColumnResized={markGridViewDirty}
          onColumnVisible={markGridViewDirty}
          onColumnPinned={markGridViewDirty}
          onSortChanged={markGridViewDirty}
          onColumnRowGroupChanged={onColumnRowGroupChanged}
          onColumnPivotChanged={markGridViewDirty}
          onColumnPivotModeChanged={markGridViewDirty}
          onColumnValueChanged={markGridViewDirty}
          onGridPreDestroyed={saveTableState}
          processUnpinnedColumns={noUnpinnedColumns}
          onFirstDataRendered={onFirstDataRendered}
          onGridReady={onInternalGridReady}
          pagination={pagination}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          paginationAutoPageSize={paginationAutoPageSize}
          rowData={rowData === undefined ? EMPTY_ARRAY : rowData}
          loading={loading}
          statusBar={statusBar}
          suppressScrollOnNewData
          suppressAggFuncInHeader={suppressAggFuncInHeader}
          rowSelection={rowSelectionOptions}
          selectionColumnDef={rowsSelectable ? selectionColumnDef : undefined}
          onSelectionChanged={handleSelectionChanged}
          enableCellTextSelection
          context={sharedContext}
          sideBar={sideBar}
          onToolPanelVisibleChanged={onToolPanelVisibleChanged}
          rowGroupPanelShow="onlyWhenGrouping"
          getContextMenuItems={getContextMenuItems}
          onCellValueChanged={onCellValueChanged}
          stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
          singleClickEdit={singleClickEdit}
          rowClassRules={rowClassRules}
          masterDetail={masterDetail}
          detailCellRendererParams={detailCellRendererParams}
          getRowHeight={getRowHeight}
          // autoSizeColumnsOnMobile forces column virtualisation off so
          // off-screen columns are rendered and autoSizeAllColumns can
          // measure their cells.
          suppressColumnVirtualisation={
            suppressColumnVirtualisation || autoSizeColumnsOnMobile
          }
          excelStyles={excelStyles}
          suppressCellFocus={suppressCellSelection}
          suppressDragLeaveHidesColumns={!!isMobile}
          suppressCsvExport={!hasExportCsvPermissions}
          suppressExcelExport={!hasExportCsvPermissions}
          defaultCsvExportParams={defaultCsvExportParams}
          defaultExcelExportParams={defaultExcelExportParams}
          enableBrowserTooltips={enableBrowserTooltips}
          initialState={stableSavedViewStates?.initialState}
        />
      </WrapperComponent>
    </>
  );
};

export default AgGridTable;
